import { useMemo, useState, type ReactElement } from 'react'
import { isNotDefined } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { TextInput } from '@mantine/core'
import { usePermissionCheck, usePreference, useProfile, useTeamListState, useTranslation } from '@front/model'
import { AddTeamButton, NotReady, Page, PermissionTooltip, TAlert, TButton } from '@components'
import { TeamsMoreMenu } from './TeamsMoreMenu'
import { TeamContent } from './TeamContent'
import { ConfigureLevelsPage } from './ConfigureLevelsPage'

const TEAM_VIEW_KEY = 'TEAM_VIEW_PREFERENCE'

function TeamsListPage(): ReactElement {
  const { t } = useTranslation()
  const { company, error, isLoading } = useProfile()
  const { data: managerBasedTeams, isLoading: isLoadingPref } = usePreference('managerBasedTeams')

  const {
    data: teams,
    isLoading: isLoadingTeams,
    error: errorTeams,
    filtered,
    filter,
    setFilter,
    refetch,
    isFetching,
  } = useTeamListState()

  const shouldConfigureLevels = useMemo(() => {
    if (managerBasedTeams) return false
    return isNotDefined(company?.highestTeamLevel) || isNotDefined(company?.lowestTeamLevel)
  }, [company, managerBasedTeams])

  const { data: canUpdateCompany } = usePermissionCheck({
    permissionType: 'company:update',
    targetId: company?.companyId,
  })

  const [showHierarchy, toggle] = useTeamView()

  if (!company) {
    return <NotReady data={company} error={error} isLoading={isLoading} />
  }

  if (isLoadingPref) {
    return <NotReady isLoading={isLoadingPref} />
  }

  const companyId = company.companyId

  if (isLoadingTeams || errorTeams || !teams) {
    return <NotReady data={teams} error={errorTeams} id={t('teams')} isLoading={isLoadingTeams} />
  }

  if (shouldConfigureLevels) {
    return <ConfigureLevelsPage canConfigure={canUpdateCompany} company={company} />
  }

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title={t('Teams')}>
        <TButton icon={showHierarchy ? 'list' : 'account_tree'} variant='secondary' onClick={toggle}>
          {t('Switch to {{view}}', { view: showHierarchy ? t('List') : t('Hierarchy') })}
        </TButton>
        <PermissionTooltip permissionType='team:add' targetId={companyId}>
          <AddTeamButton level={company.highestTeamLevel ?? 2} />
        </PermissionTooltip>
        <TeamsMoreMenu
          canManageLevels={Boolean(canUpdateCompany) && !managerBasedTeams}
          company={company}
          managerBasedTeams={managerBasedTeams ?? false}
          teams={teams}
        />
      </Page.Heading>
      {!showHierarchy && (
        <Page.FunctionBar justify='end'>
          <TextInput
            flex={1}
            placeholder={t('Filter...')}
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
          />
        </Page.FunctionBar>
      )}
      <Page.Content noPadding>
        <TeamContent
          managerBasedTeams={managerBasedTeams ?? false}
          canUpdateCompany={canUpdateCompany}
          company={company}
          hierarchyTeams={teams}
          listTeams={filtered}
          showHierarchy={showHierarchy}
          footer={
            canUpdateCompany && (
              <TAlert variant='discovery'>
                {t(
                  'Changing team hierarchy and team members may temporarily affect stats and calendar events list when filtering by team. All affected results will be updated within 24 hours.'
                )}
              </TAlert>
            )
          }
        />
      </Page.Content>
    </Page>
  )
}

const useTeamView = (): [boolean, VoidFunction] => {
  const stored = window.localStorage.getItem(TEAM_VIEW_KEY)
  const [preference, setPreference] = useState<boolean>(stored === 'hierarchy')

  const toggle = (): void => {
    setPreference((prev) => {
      window.localStorage.setItem(TEAM_VIEW_KEY, prev ? 'list' : 'hierarchy')
      return !prev
    })
  }

  return [preference, toggle]
}

export const Route = createFileRoute('/team/')({
  component: TeamsListPage,
})
